import { Outlet, Link, NavLink, useNavigate, Navigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { portalApi } from "@/lib/portalApi";
import { NotificationBell } from "@/components/NotificationBell";
import { cn } from "@/lib/utils";

const TABS = [["/portal", "Jobs", true], ["/portal/quotes", "Quotes"], ["/portal/invoices", "Invoices"], ["/portal/profile", "Profile"]];

export default function PortalLayout() {
  const { account, logout } = usePortalAuth();
  const navigate = useNavigate();
  if (account === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (!account) return <Navigate to="/portal/login" replace />;
  const t = account.tenant;
  const brand = t.brand_color || "#13294B";
  return (
    <div className="min-h-screen bg-slate-50" style={{ "--brand": brand }}>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md" style={{ borderTop: `4px solid ${brand}` }}>
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/portal" className="flex min-w-0 items-center gap-3" data-testid="portal-home-link">
            {t.logo ? <img src={t.logo} alt={t.name} className="h-9 max-w-[120px] object-contain" data-testid="portal-tenant-logo" />
              : <span className="flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold text-white" style={{ background: brand }}>{t.name.slice(0, 2).toUpperCase()}</span>}
            <span className="truncate text-sm font-semibold text-slate-800" data-testid="portal-tenant-name">{t.name}<span className="hidden font-normal text-slate-500 sm:inline"> · Client portal</span></span>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden text-slate-500 md:block" data-testid="portal-account-name">{account.customer_name}</span>
            <NotificationBell client={portalApi} testId="portal-bell" />
            <button data-testid="portal-logout-button" onClick={async () => { await logout(); navigate("/portal/login"); }} className="flex items-center gap-1 rounded-md px-2 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 sm:px-6">
          {TABS.map(([to, label, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`portal-tab-${label.toLowerCase()}`}
              className={({ isActive }) => cn("border-b-2 px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}
              style={({ isActive }) => (isActive ? { borderColor: brand } : undefined)}>{label}</NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6"><Outlet /></main>
    </div>
  );
}
